/**
 * SwitchSlotChildren 子组件
 * 渲染切换控件对应的内容区域，支持两种渲染模式：
 * 1. byDataKey 模式（SegmentCo 等）：根据当前激活值从 switchSlot.children 中过滤并渲染匹配的子组件
 * 2. 默认模式（SwitchStateWrap 等）：从 switchSlotProps.componentMap 中按 cardTitleSwitchVal 获取子组件列表
 *
 * 当传入 loadingTargetNames 时，会把 loading/error 透传给子组件，
 * 由子组件（如 LifetimeCardContainer）根据 loadingTargetNames 精确包裹 Abnormal
 */
import React, { useMemo } from 'react';
import { IComponentType } from '../PageContainer/type';
import Render from '../PageContainer/Render';
import { SwitchSlotChildrenProps } from './type';

export default function SwitchSlotChildren({
  switchSlot,
  switchSlotProps,
  shouldRenderByDataKey,
  data,
  switchData,
  isChange,
  loading,
  error,
  loadingTargetNames,
  switchDataLoadingHeight = 454,
  changeData,
}: SwitchSlotChildrenProps) {
  // 根据渲染模式计算需要展示的子组件列表
  const childrenList = useMemo(() => {
    if (shouldRenderByDataKey) {
      // byDataKey 模式：从 switchSlot.children 中按激活值过滤
      const childrenDataKey = switchSlotProps?.childrenDataKey || switchSlotProps?.config?.dataKey;
      const childrenMatchField = switchSlotProps?.childrenMatchField || 'id';
      const optionList = switchSlotProps?.config?.options || [];
      const defaultValue = switchSlotProps?.childrenDefaultValue || optionList?.[0]?.value;
      const activeValue = (childrenDataKey && (switchData?.[childrenDataKey] || data?.[childrenDataKey])) || defaultValue;

      // 过滤出匹配当前激活值的子组件，无 matchField 的子组件始终展示
      return (switchSlot?.children || []).filter((item: any) => {
        if (!item?.[childrenMatchField]) return true;
        return item[childrenMatchField] === activeValue;
      });
    }

    // 默认模式：通过 cardTitleSwitchVal 从 componentMap 中获取对应组件列表
    return switchSlotProps?.componentMap?.[switchData?.cardTitleSwitchVal] || [];
  }, [switchSlot?.children, switchSlotProps, shouldRenderByDataKey, data, switchData]);

  // 精确 loading 控制所需的额外 props
  const hasTargetLoading = loadingTargetNames && loadingTargetNames.length > 0;
  const loadingExtraProps = hasTargetLoading ? {
    parentLoading: loading,
    parentError: error,
    loadingTargetNames,
    switchDataLoadingHeight,
  } : {};

  return (
    <>
      {childrenList?.map((item: IComponentType | undefined, index: number) => (
        <Render
          config={{ ...(item || {}), switchData } as any}
          key={(item?.name || 'item') + ((item as any)?.id || '') + index}
          data={data}
          changeData={changeData}
          {...(isChange !== undefined ? { isChange } as any : {})}
          {...loadingExtraProps as any}
        />
      ))}
    </>
  );
}
